import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DeveloperService, EnvironmentService, EventService, NotificationService } from '@core/services';

import { AdminService } from '@admin/services';
import { CONSTANTS } from '@auth/constants';
import { AuthService, AuthUtilService } from '@auth/services';
import { AuthActions } from '@auth/state';

@Component({
  selector: 'z-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  loading$ = new BehaviorSubject<boolean>(false);

  layer1Style$ = new BehaviorSubject<any>({ opacity: 1 });
  layer2Style$ = new BehaviorSubject<any>({ opacity: 0 });

  form: FormGroup;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private adminService: AdminService,
    public environmentService: EnvironmentService,
    private notificationService: NotificationService,
    private store: Store,
    private translocoService: TranslocoService,
    public developerService: DeveloperService,
    private router: Router,
    private titleService: Title,
    private authUtilService: AuthUtilService,
    private eventService: EventService
  ) {}

  ngOnInit() {
    this.authService.authenticated$
      .pipe(
        withLatestFrom(this.loading$),
        filter(([authenticated, loading]) => !!authenticated && !loading),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        void this.router.navigate(['/home']);
      });

    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.titleService.setTitle(this.translate('core.layout.title'));
      });

    this.form = new FormGroup({
      username: new FormControl(null, Validators.required),
      password: new FormControl(null, Validators.required),
    });

    // load theme for login panel if the user already has the session state available
    this.authService.theme$.pipe(takeUntil(this.destroy$)).subscribe((theme: string) => {
      this.adminService
        .getImage(`login-background-${theme}`)
        .pipe(take(1))
        .subscribe((response) => {
          if (response.status === 204 || response.status >= 400) {
            this.layer1Style$.next({ opacity: 1, backgroundImage: `url(./assets/backgrounds/background-${theme}.jpg)` });
            this.layer2Style$.next({ opacity: 0 });
          } else {
            const reader = new FileReader();
            reader.readAsDataURL(response.body);
            reader.onloadend = () => {
              this.layer1Style$.next({ opacity: 0, backgroundImage: `url(./assets/backgrounds/background-${theme}.jpg)` });
              this.layer2Style$.next({ opacity: 1, backgroundImage: `url(${reader.result.toString()})` });
            };
          }
        });
    });

    this.eventService
      .loading(CONSTANTS.REQUEST_AUTH_LOGON)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        if (loading) {
          this.form.disable();
        } else {
          this.form.enable();
        }
      });

    this.eventService
      .loading(CONSTANTS.REQUEST_AUTH_GET_PERMISSIONS)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        if (loading) {
          this.form.disable();
        } else {
          this.form.enable();
        }
      });

    // login success
    this.authService.authenticated$
      .pipe(
        filter((x) => x),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.form.setValue({ username: '', password: '' });
        }, 1000);
      });

    // login failure
    this.authService.loginError$
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((error) => {
        this.loading$.next(false);
        this.form.enable();

        const { title, message } = this.authUtilService.getErrorMessage(error);

        this.notificationService.error({
          title,
          message,
        });
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  login() {
    if (!this.form.get('username').value || !this.form.get('password').value) {
      this.notificationService.error({
        message: this.translate('auth.login.error_messages.fields_cannot_be_blank'),
      });

      return;
    }

    this.loading$.next(true);

    this.store.dispatch(AuthActions.Login({ username: this.form.get('username').value, password: this.form.get('password').value }));
  }

  automaticLogin = () => {
    if (this.environmentService.environment.production) {
      return;
    }

    this.form.setValue(this.environmentService.credentials);
  };

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.environmentService.environment.production) {
      return;
    }

    if (event.key === 'S' && event.ctrlKey && event.shiftKey) {
      this.developerService.clearState();
    }

    if (event.key === 'F4') {
      this.developerService.toggleTheme();
    }

    if (event.key === 'F9') {
      this.developerService.toggleLanguage();
    }
  }
}
